import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { usePageTitle } from '../context/PageTitleContext';
import { DateRangePicker, Select } from './common';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { FiFilter, FiCalendar, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';

const AnalyticsDashboard = () => {
  const { isCollapsed } = useSidebar();
  const { setPageTitle, setPageSubtitle } = usePageTitle();
  const { user, hasPagePermission } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    filter: '1W', // '1D', '1W', '1M', 'custom'
    start_date: '',
    end_date: '',
    region_code: ''
  });

  const [filterOptions, setFilterOptions] = useState({
    regions: []
  });

  // "View All" is offered per section, and only when this user's role actually
  // has the matching report page. It used to key off role ids, which meant it
  // showed for role 2 or 3 whatever their permissions said — and role ids
  // differ between databases anyway.
  const canOpenReport = (route) =>
    hasPagePermission(route, 'can_list') || hasPagePermission(route, 'can_view');

  // BioMedical calls a region an Entity, and its team report sits on a
  // different route from IT's.
  const isBioMedical = (user?.department_code || 'IT') === 'BM';
  const regionLabel = isBioMedical ? 'Entity' : 'Regional';
  const teamLabel = isBioMedical ? 'BioMedical' : 'IT';
  // IT groups by Region, BioMedical by Entity
  const regionWord = isBioMedical ? 'Entity' : 'Region';
  const regionsWord = isBioMedical ? 'Entities' : 'Regions';
  const teamReportRoute = isBioMedical ? '/reports/team' : '/reports/it-team';

  const [dashboardData, setDashboardData] = useState({
    summary: {},
    ticketVolume: {},
    statusSnapshot: {},
    resolutionTime: {},
    categoryBreakdown: {},
    regionalPerformance: {},
    teamPerformance: {},
    itTeamPerformance: {}
  });

  // Format a local date as YYYY-MM-DD without time                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                               one shifting
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return formatLocalDate(new Date());
  };

  // Helper function to get start of week (Monday)
  const getWeekStartDate = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return formatLocalDate(monday);
  };

  // Helper function to get start of month
  const getMonthStartDate = () => {
    const today = new Date();
    return formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  useEffect(() => {
    setPageTitle('Dashboard');
    setPageSubtitle('Real-time insights and performance metrics');
    fetchFilterOptions();
    // Set initial date range for 1W
    handlePresetFilter('1W');
  }, [setPageTitle, setPageSubtitle]);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await dashboardService.getFilters();
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params = { ...filters };

      const [
        summaryRes,
        volumeRes,
        statusRes,
        resolutionRes,
        categoryRes,
        regionalRes,
        teamRes,
        itTeamRes
      ] = await Promise.all([
        dashboardService.getSummary(params),
        dashboardService.getTicketVolume(params),
        dashboardService.getStatusSnapshot(params),
        dashboardService.getResolutionTime(params),
        dashboardService.getCategoryBreakdown(params),
        dashboardService.getRegionalPerformance(params),
        dashboardService.getTeamPerformance(params),
        dashboardService.getItTeamPerformance(params)
      ]);

      setDashboardData({
        summary: summaryRes.data,
        ticketVolume: volumeRes.data,
        statusSnapshot: statusRes.data,
        resolutionTime: resolutionRes.data,
        categoryBreakdown: categoryRes.data,
        regionalPerformance: regionalRes.data,
        teamPerformance: teamRes.data,
        itTeamPerformance: itTeamRes.data
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const navigateToTicketsWithFilters = (status) => {
    const params = new URLSearchParams();

    if (filters.start_date) params.set('start_date', filters.start_date);
    if (filters.end_date) params.set('end_date', filters.end_date);
    if (filters.region_code) params.set('region_code', filters.region_code);
    if (status) params.set('status', status);

    navigate(`/tickets${params.toString() ? `?${params.toString()}` : ''}`);
  };

  // Handle preset filter buttons (1D, 1W, 1M)
  const handlePresetFilter = (filterType) => {
    const today = getTodayDate();
    let startDate = '';

    switch (filterType) {
      case '1D':
        startDate = today;
        break;
      case '1W':
        startDate = getWeekStartDate();
        break;
      case '1M':
        startDate = getMonthStartDate();
        break;
      default:
        startDate = '';
    }

    setFilters(prev => ({
      ...prev,
      filter: filterType,
      start_date: startDate,
      end_date: today
    }));
  };

  // Handle manual date change
  const handleDateChange = (key, value) => {
    if (key === 'start_date') {
      // When from date changes, clear end_date if it's less than the new start_date
      setFilters(prev => ({
        ...prev,
        filter: 'custom',
        start_date: value,
        end_date: prev.end_date && prev.end_date < value ? '' : prev.end_date
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        filter: 'custom',
        [key]: value
      }));
    }
  };


  if (loading && !dashboardData.summary.totalTickets) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Sticky Filters */}
      <div className="sticky top-0 z-40 bg-gray-50 pb-4 -mt-2 pt-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Time Period Buttons */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handlePresetFilter('1D')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filters.filter === '1D'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              1D
            </button>
            <button
              onClick={() => handlePresetFilter('1W')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filters.filter === '1W'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              1W
            </button>
            <button
              onClick={() => handlePresetFilter('1M')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filters.filter === '1M'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              1M
            </button>
          </div>

          {/* Date Range Picker */}
          <div className="w-[210px] h-[38px]">
            <DateRangePicker
              startDate={filters.start_date}
              endDate={filters.end_date}
              onStartDateChange={(e) => handleDateChange('start_date', e.target.value)}
              onEndDateChange={(e) => handleDateChange('end_date', e.target.value)}
              maxDate={getTodayDate()}
              className="mb-0"
            />
          </div>

          {/* Region Filter - Select Component */}
          <div className="w-[200px]">
            <Select
              name="region_code"
              value={filters.region_code}
              onChange={(e) => handleFilterChange('region_code', e.target.value)}
              options={[
                { value: '', label: `All ${regionsWord}` },
                ...filterOptions.regions.map(region => ({
                  value: region.code,
                  label: region.name
                }))
              ]}
              placeholder={`All ${regionsWord}`}
              searchable={true}
              className="mb-0"
              style={{ marginBottom: 0 }}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards - Gradient Style (Filtered by Date Range + Region) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <div className="rounded-xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
          <h3 className="text-sm opacity-90 mb-2">Total Tickets</h3>
          <div className="text-4xl font-bold">{(dashboardData.summary.totalTickets || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-xl p-5 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }} onClick={() => navigate('/tickets?filter_by=region')}>
          <h3 className="text-sm opacity-90 mb-2">{regionsWord}</h3>
          <div className="text-4xl font-bold">{(dashboardData.summary.distinctRegions || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-xl p-5 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' }} onClick={() => navigate('/tickets?filter_by=branch')}>
          <h3 className="text-sm opacity-90 mb-2">Branches</h3>
          <div className="text-4xl font-bold">{(dashboardData.summary.distinctBranches || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
          <h3 className="text-sm opacity-90 mb-2">Categories</h3>
          <div className="text-4xl font-bold">{(dashboardData.summary.distinctCategories || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Card 1: Ticket Volume Overview */}
        <ChartCard title="Ticket Volume Overview">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/tickets?start_date=${getTodayDate()}&end_date=${getTodayDate()}`)}>
              <div className="text-xs text-gray-600 mb-1">Today</div>
              <div className="text-2xl font-bold text-gray-800">{dashboardData.ticketVolume.today || 0}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-600 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/tickets?start_date=${getWeekStartDate()}&end_date=${getTodayDate()}`)}>
              <div className="text-xs text-gray-600 mb-1">This Week</div>
              <div className="text-2xl font-bold text-gray-800">{dashboardData.ticketVolume.week || 0}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-700 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/tickets?start_date=${getMonthStartDate()}&end_date=${getTodayDate()}`)}>
              <div className="text-xs text-gray-600 mb-1">This Month</div>
              <div className="text-2xl font-bold text-gray-800">{dashboardData.ticketVolume.month || 0}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dashboardData.ticketVolume.dailyTrend || []} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} fill="#3b82f6" fillOpacity={0.1} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 text-center border-t pt-3">
            <span className="text-gray-600 text-sm">Avg per day: </span>
            <span className="text-lg font-bold text-gray-800">{dashboardData.ticketVolume.avgPerDay || 0}</span>
          </div>
          <ConclusionBox insights={[
            `Total tickets in selected period: ${dashboardData.ticketVolume.dailyTrend?.reduce((sum, d) => sum + d.count, 0) || 0}`,
            `Average tickets per day: ${dashboardData.ticketVolume.avgPerDay || 0}`,
            `Peak day: ${dashboardData.ticketVolume.dailyTrend?.reduce((max, d) => d.count > (max?.count || 0) ? d : max, {})?.date || 'N/A'} with ${dashboardData.ticketVolume.dailyTrend?.reduce((max, d) => d.count > (max?.count || 0) ? d : max, {})?.count || 0} tickets`
          ]} />
        </ChartCard>

        {/* Card 2: Status Snapshot */}
        <ChartCard title="Ticket Status Snapshot">
          {/* Metrics Row */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-red-500">
              <div className="text-xs text-gray-600 mb-1">Pending {'>'}48 hrs</div>
              <div className="text-2xl font-bold text-gray-800">
                {dashboardData.statusSnapshot.pendingOver48h || 0}
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-orange-500">
              <div className="text-xs text-gray-600 mb-1">Reopen Rate</div>
              <div className="text-2xl font-bold text-gray-800">
                {dashboardData.statusSnapshot.reopenRate || 0}
                <span className="text-sm font-normal text-gray-600">%</span>
              </div>
            </div>
          </div>
          
          {/* Bar Chart */}
          <ResponsiveContainer width="100%" height={250}>
            <BarChart 
              data={dashboardData.statusSnapshot.statuses || []}
              margin={{ top: 5, right: 5, left: -15, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value, name, props) => [`${value} tickets (${props.payload.percentage}%)`, 'Count']} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(barData) => { if (barData && barData.status) navigateToTicketsWithFilters(barData.status); }}>
                {dashboardData.statusSnapshot.statuses?.map((status, index) => (
                  <Cell key={`cell-${index}`} fill={status.color || '#3b82f6'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Conclusion Box */}
          <ConclusionBox insights={[
            `${dashboardData.statusSnapshot.pendingOver48h || 0} tickets pending over 48 hours`,
            `Reopen rate: ${dashboardData.statusSnapshot.reopenRate || 0}%`,
            `Total tickets in period: ${dashboardData.statusSnapshot.totalTickets || 0}`
          ]} />
        </ChartCard>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Card 3: Resolution Time */}
        <ChartCard title="Resolution Time Analysis">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-500">
              <div className="text-xs text-gray-600 mb-1">Avg First Response</div>
              <div className="text-2xl font-bold text-gray-800">
                {dashboardData.resolutionTime.avgFirstResponseHours || 0}
                <span className="text-sm font-normal text-gray-600">hrs</span>
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-600">
              <div className="text-xs text-gray-600 mb-1">Avg Resolution</div>
              <div className="text-2xl font-bold text-gray-800">
                {dashboardData.resolutionTime.avgResolutionHours || 0}
                <span className="text-sm font-normal text-gray-600">hrs</span>
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-700">
              <div className="text-xs text-gray-600 mb-1">Median Resolution</div>
              <div className="text-2xl font-bold text-gray-800">
                {dashboardData.resolutionTime.medianResolutionHours || 0}
                <span className="text-sm font-normal text-gray-600">hrs</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                data={dashboardData.resolutionTime.dailyResolutionTrend || []}
                dataKey="hours" 
                stroke="#3b82f6" 
                strokeWidth={3} 
                name="Resolution Time"
              />
              <Line 
                type="monotone"
                data={dashboardData.resolutionTime.dailyFirstResponseTrend || []}
                dataKey="hours" 
                stroke="#60a5fa" 
                strokeWidth={3} 
                name="First Response"
              />
            </LineChart>
          </ResponsiveContainer>
          <ConclusionBox insights={[
            `Fastest response time: ${dashboardData.resolutionTime.fastestResponseHours || 0} hours`,
            `Slowest response time: ${dashboardData.resolutionTime.slowestResponseHours || 0} hours`,
            `Fastest resolution time: ${dashboardData.resolutionTime.fastestResolutionHours || 0} hours`,
            `Slowest resolution time: ${dashboardData.resolutionTime.slowestResolutionHours || 0} hours`
          ]} />
        </ChartCard>

        {/* Card 4: Category Breakdown */}
        <ChartCard
          title="Category Breakdown"
          viewAllLink={canOpenReport('/reports/category') ? () => navigate('/reports/category') : undefined}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={dashboardData.categoryBreakdown.categories || []}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value, name, props) => {
                  const trend = props.payload.trendPercentage;
                  const trendStr = trend > 0 ? `+${trend}%` : `${trend}%`;
                  return [`${value} tickets (${trendStr} vs prev period)`, 'Count'];
                }} 
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} label={({ x, y, width, height, value, index }) => {
                const categories = dashboardData.categoryBreakdown.categories || [];
                const cat = categories[index];
                if (!cat) return null;
                const trend = cat.trendPercentage || 0;
                const isUp = trend > 0;
                const isDown = trend < 0;
                const color = isUp ? '#ef4444' : isDown ? '#10b981' : '#6b7280';
                const arrow = isUp ? '▲' : isDown ? '▼' : '–';
                return (
                  <text x={x + width + 5} y={y + height / 2 + 4} fill={color} fontSize={11} fontWeight="600">
                    {arrow} {Math.abs(trend)}%
                  </text>
                );
              }}>
                {dashboardData.categoryBreakdown.categories?.map((entry, index) => {
                  const CATEGORY_COLORS = [
                    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
                    '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
                  ];
                  return <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <ConclusionBox insights={
            (dashboardData.categoryBreakdown.categories || []).slice(0, 3).map(cat => {
              const trend = cat.trendPercentage || 0;
              const direction = trend > 0 ? 'increased' : trend < 0 ? 'decreased' : 'unchanged';
              return `${cat.name}: ${cat.count} tickets (${cat.percentage}%) — ${direction} by ${Math.abs(trend)}% vs previous period`;
            })
          } />
        </ChartCard>
      </div>

      {/* Row 3: Regional Performance Table */}
      <div className="grid grid-cols-1 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div>
                <h3 className="text-lg font-semibold">{regionLabel} Performance (Top 10)</h3>
                <p className="text-xs text-gray-500">Performance based on escalations, reopen rate &amp; resolution SLA</p>
              </div>
            </div>
            {canOpenReport('/reports/region') && (
              <span onClick={() => navigate('/reports/region')} className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800 transition-colors">
                View All &rarr;
              </span>
            )}
          </div>
          {/* Rating Legend */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Excellent: Score &ge; 75%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Average: Score &ge; 50%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Poor: Score &lt; 50%</span>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded mb-4">
            <p className="text-xs text-blue-800">
              <strong>How it works:</strong> Status is derived from: Escalation Rate (lower = better), Reopen Rate (lower = better), and Resolution SLA % (higher = better).
              Overall Score = weighted average: Resolution SLA 40% + (100 - Reopen Rate) 30% + (100 - Escalation Rate) 30%.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{regionWord}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employees</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets/Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Escalations</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reopen Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolution SLA %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.regionalPerformance.regions?.map((region) => {
                  const escalationRate = region.totalTickets > 0 ? (region.escalatedCount / region.totalTickets) * 100 : 0;
                  const reopenRate = region.reopenRate || 0;
                  const resPct = region.resolutionPct || 0;
                  const weightedScore = Math.round(
                    (resPct * 0.4) + (Math.max(0, 100 - reopenRate) * 0.3) + (Math.max(0, 100 - escalationRate) * 0.3)
                  );
                  return { ...region, escalationRate, reopenRate, resPct, weightedScore };
                }).sort((a, b) => b.weightedScore - a.weightedScore).map((region, index) => {
                  const { escalationRate, weightedScore } = region;
                  const status = weightedScore >= 75 ? 'Excellent' : weightedScore >= 50 ? 'Average' : 'Poor';
                  const statusColor = status === 'Excellent' ? 'bg-green-100 text-green-800' : status === 'Average' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{region.regionName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{region.employeeCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">{region.totalTickets}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">{region.ticketsPerEmployee}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <span className="font-medium">{region.totalClosed}</span>
                        <span className="text-gray-400"> / {region.totalTickets}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-semibold ${region.escalatedCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {region.escalatedCount || 0}
                        </span>
                        {region.totalTickets > 0 && (
                          <span className="text-gray-400 text-xs ml-1">({Math.round(escalationRate)}%)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-medium ${region.reopenRate > 10 ? 'text-red-600' : region.reopenRate > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {region.reopenRate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[80px]">
                            <div
                              className="h-2 rounded-full transition-all duration-500"
                              style={{
                                width: `${Math.min(region.resolutionPct, 100)}%`,
                                backgroundColor: region.resolutionPct >= 80 ? '#10b981' : region.resolutionPct >= 50 ? '#f59e0b' : '#ef4444'
                              }}
                            ></div>
                          </div>
                          <span className="font-medium" style={{ color: region.resolutionPct >= 80 ? '#059669' : region.resolutionPct >= 50 ? '#d97706' : '#dc2626' }}>
                            {region.resolutionPct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-lg font-bold" style={{ color: weightedScore >= 75 ? '#059669' : weightedScore >= 50 ? '#d97706' : '#dc2626' }}>
                          {weightedScore}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm sticky right-0 bg-white">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusColor}`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ConclusionBox insights={[
            `Top region: ${dashboardData.regionalPerformance.regions?.[0]?.regionName || 'N/A'} with ${dashboardData.regionalPerformance.regions?.[0]?.totalTickets || 0} tickets`,
            `Total escalations: ${dashboardData.regionalPerformance.regions?.reduce((sum, r) => sum + (r.escalatedCount || 0), 0) || 0}`,
            `Good regions: ${dashboardData.regionalPerformance.regions?.filter(r => { const er = r.totalTickets > 0 ? (r.escalatedCount / r.totalTickets) * 100 : 0; const ws = Math.round((r.resolutionPct || 0) * 0.3 + (r.firstResponsePct || 0) * 0.3 + Math.max(0, 100 - (r.reopenRate || 0)) * 0.2 + Math.max(0, 100 - er) * 0.2); return ws >= 75; }).length || 0}`,
            `Average regions: ${dashboardData.regionalPerformance.regions?.filter(r => { const er = r.totalTickets > 0 ? (r.escalatedCount / r.totalTickets) * 100 : 0; const ws = Math.round((r.resolutionPct || 0) * 0.3 + (r.firstResponsePct || 0) * 0.3 + Math.max(0, 100 - (r.reopenRate || 0)) * 0.2 + Math.max(0, 100 - er) * 0.2); return ws >= 50 && ws < 75; }).length || 0}`,
            `Poor regions: ${dashboardData.regionalPerformance.regions?.filter(r => { const er = r.totalTickets > 0 ? (r.escalatedCount / r.totalTickets) * 100 : 0; const ws = Math.round((r.resolutionPct || 0) * 0.3 + (r.firstResponsePct || 0) * 0.3 + Math.max(0, 100 - (r.reopenRate || 0)) * 0.2 + Math.max(0, 100 - er) * 0.2); return ws < 50; }).length || 0}`,
            `Total regions tracked: ${dashboardData.regionalPerformance.regions?.length || 0}`
          ]} />
        </div>
      </div>

      {/* Row 4: Team Performance Table */}
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div>
                <h3 className="text-lg font-semibold">{teamLabel} Team Performance (Top 10)</h3>
                <p className="text-xs text-gray-500">Performance based on SLA rate, escalations &amp; reopen rate</p>
              </div>
            </div>
            {canOpenReport(teamReportRoute) && (
              <span onClick={() => navigate(teamReportRoute)} className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800 transition-colors">
                View All &rarr;
              </span>
            )}
          </div>
          {/* Rating Legend */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Excellent: Score &ge; 75%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Average: Score &ge; 50%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Poor: Score &lt; 50%</span>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded mb-4">
            <p className="text-xs text-blue-800">
              <strong>Currently Assigned</strong> = Tickets currently with the engineer.
              <strong> Total Handled</strong> = Currently assigned + Escalated away + Reopened away.
            </p>
            <p className="text-xs text-blue-800 mt-1">
              <strong>SLA Rate</strong> = Resolved within SLA without escalations / Currently Assigned.
              <strong> Escalation Rate</strong> = Weighted by severity (Level 1=1pt, Level 2-3=2pts, Level 4+=3pts).
            </p>
            <p className="text-xs text-blue-800 mt-1">
              <strong>Resolved</strong> = Tickets closed by engineer (current assignment).
              <strong> Reopen Rate</strong> = Tickets reopened after engineer closed them / Closed by engineer.
            </p>
            <p className="text-xs text-blue-800 mt-1">
              <strong>Overall Score</strong> = SLA Rate 40% + (100 - Escalation Rate) 30% + (100 - Reopen Rate) 30%.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engineer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Currently Assigned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Handled</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SLA Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Escalation Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reopen Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.itTeamPerformance.engineers
                  ?.sort((a, b) => {
                    // Status priority: Excellent (1) -> Average (2) -> Poor (3)
                    const getStatusPriority = (engineer) => {
                      if (engineer.overallScore >= 75) return 1; // Excellent
                      if (engineer.overallScore >= 50) return 2; // Average
                      return 3; // Poor
                    };

                    const priorityA = getStatusPriority(a);
                    const priorityB = getStatusPriority(b);

                    // First sort by status priority (ascending: Excellent first)
                    if (priorityA !== priorityB) {
                      return priorityA - priorityB;
                    }

                    // Then sort by overall score (descending: higher scores first)
                    return b.overallScore - a.overallScore;
                  })
                  ?.map((engineer, index) => {
                  const status = engineer.overallScore >= 75 ? 'Excellent' : engineer.overallScore >= 50 ? 'Average' : 'Poor';
                  const statusColor = status === 'Excellent' ? 'bg-green-100 text-green-800' : status === 'Average' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{engineer.name}</td>
                      <td className="px-4 py-3 text-sm text-blue-700 font-semibold cursor-pointer hover:underline" onClick={() => { const p = new URLSearchParams(); p.set('assigned_user_id', engineer.userId); if (filters.start_date) p.set('start_date', filters.start_date); if (filters.end_date) p.set('end_date', filters.end_date); if (filters.region_code) p.set('region_code', filters.region_code); navigate(`/tickets?${p.toString()}`); }} title="View currently assigned tickets">{engineer.currentlyAssigned}</td>
                      <td className="px-4 py-3 text-sm text-blue-700 font-semibold cursor-pointer hover:underline" onClick={() => { const p = new URLSearchParams(); p.set('assigned_user_id', engineer.userId); if (filters.start_date) p.set('start_date', filters.start_date); if (filters.end_date) p.set('end_date', filters.end_date); if (filters.region_code) p.set('region_code', filters.region_code); navigate(`/tickets?${p.toString()}`); }} title="View all handled tickets">{engineer.totalHandled}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-green-600 cursor-pointer hover:underline" onClick={() => { const p = new URLSearchParams(); p.set('assigned_user_id', engineer.userId); p.set('status', 'Closed,Resolved'); if (filters.start_date) p.set('start_date', filters.start_date); if (filters.end_date) p.set('end_date', filters.end_date); if (filters.region_code) p.set('region_code', filters.region_code); navigate(`/tickets?${p.toString()}`); }} title="View resolved tickets">{engineer.resolved}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[80px]">
                            <div
                              className="h-2 rounded-full transition-all duration-500"
                              style={{
                                width: `${Math.min(engineer.slaRate, 100)}%`,
                                backgroundColor: engineer.slaRate >= 80 ? '#10b981' : engineer.slaRate >= 50 ? '#f59e0b' : '#ef4444'
                              }}
                            ></div>
                          </div>
                          <span className="font-medium" style={{ color: engineer.slaRate >= 80 ? '#059669' : engineer.slaRate >= 50 ? '#d97706' : '#dc2626' }}>
                            {engineer.slaRate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-semibold cursor-pointer hover:underline ${engineer.escalationRate > 20 ? 'text-red-600' : engineer.escalationRate > 10 ? 'text-yellow-600' : 'text-green-600'}`} onClick={() => { const p = new URLSearchParams(); p.set('assigned_user_id', engineer.userId); p.set('escalated', 'true'); if (filters.start_date) p.set('start_date', filters.start_date); if (filters.end_date) p.set('end_date', filters.end_date); if (filters.region_code) p.set('region_code', filters.region_code); navigate(`/tickets?${p.toString()}`); }} title="View escalated tickets">
                          {engineer.escalationRate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-medium ${engineer.reopenRate > 10 ? 'text-red-600' : engineer.reopenRate > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {engineer.reopenRate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-lg font-bold" style={{ color: engineer.overallScore >= 75 ? '#059669' : engineer.overallScore >= 50 ? '#d97706' : '#dc2626' }}>
                          {engineer.overallScore}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm sticky right-0 bg-white">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusColor}`}>
                          {engineer.statusLabel || status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ConclusionBox insights={[
            `Top performer: ${dashboardData.itTeamPerformance.engineers?.[0]?.name || 'N/A'} with ${dashboardData.itTeamPerformance.engineers?.[0]?.totalHandled || 0} tickets handled`,
            `Total escalations: ${dashboardData.itTeamPerformance.engineers?.reduce((sum, e) => sum + (e.escalatedCount || 0), 0) || 0}`,
            `Excellent performers: ${dashboardData.itTeamPerformance.engineers?.filter(e => e.overallScore >= 75).length || 0}`,
            `Average performers: ${dashboardData.itTeamPerformance.engineers?.filter(e => e.overallScore >= 50 && e.overallScore < 75).length || 0}`,
            `Poor performers: ${dashboardData.itTeamPerformance.engineers?.filter(e => e.overallScore < 50).length || 0}`,
            `Engineers tracked: ${dashboardData.itTeamPerformance.engineers?.length || 0}`
          ]} />
        </div>
      </div>
    </div>
  );
};

// Helper Components
const SummaryCard = ({ title, value, icon, color }) => (
  <div className="bg-white rounded-lg shadow p-6 flex items-center">
    <div className={`${color} rounded-lg p-4 text-white text-3xl mr-4`}>
      {icon}
    </div>
    <div className="flex-1">
      <p className="text-gray-600 text-sm">{title}</p>
      <p className="text-3xl font-bold text-gray-800">{value.toLocaleString()}</p>
    </div>
  </div>
);

const ChartCard = ({ title, children, viewAllLink }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <h3 className="text-lg font-semibold px-4 pt-4 pb-2 flex items-center justify-between gap-2">
      <span>{title}</span>
      {viewAllLink && (
        <span onClick={viewAllLink} className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800 transition-colors">
          View All &rarr;
        </span>
      )}
    </h3>
    <div className="px-2 pb-4">
      {children}
    </div>
  </div>
);

const ConclusionBox = ({ insights }) => (
  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mt-4">
    <h4 className="text-sm font-semibold text-blue-800 mb-2">📌 Key Insights</h4>
    <ul className="space-y-1">
      {insights.map((insight, index) => (
        <li key={index} className="text-xs text-blue-900 flex items-start gap-2">
          <span className="text-blue-600 font-bold">•</span>
          <span>{insight}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default AnalyticsDashboard;
